import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../Services/supabaseClient";
import { Link, useSearchParams } from "react-router-dom";
import { useCompany } from "../../context/CompanyContext";
import { getAccounts } from "../services/crmAccountsService";
import { getOpportunities } from "../services/crmOpportunitiesService";
import { createQuote, getQuotes } from "../services/crmQuotesService";
import { getCrmSettings } from "../services/crmSettingsService";
import QuoteForm from "../components/QuoteForm";
import "../styles/crm.css";

const QuotesPage = () => {
  const { companyId } = useCompany();
  const [searchParams] = useSearchParams();

  const [quotes, setQuotes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [accountFilter, setAccountFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [crmSettings, setCrmSettings] = useState(null);

  const prefillOpportunityId = searchParams.get("opportunityId") || "";

  const load = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setMessage("");

    try {
      const [
        quoteRows,
        accountRows,
        opportunityRows,
        contactsRes,
        settingsRow,
      ] = await Promise.all([
        getQuotes(companyId),
        getAccounts(companyId),
        getOpportunities(companyId),
        supabase
          .from("crm_contacts")
          .select("*")
          .eq("company_id", companyId)
          .order("full_name", { ascending: true }),
        getCrmSettings(companyId),
      ]);

      if (contactsRes.error) {
        throw contactsRes.error;
      }

      setQuotes(quoteRows);
      setAccounts(accountRows);
      setOpportunities(opportunityRows);
      setContacts(contactsRes.data || []);
      setCrmSettings(settingsRow);
    } catch (error) {
      setMessage(error.message || "Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateQuote = useCallback(
    async (payload) => {
      if (!companyId) return;

      setSaving(true);
      setMessage("");

      try {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) {
          throw userError;
        }

        if (!user?.id) {
          throw new Error("User not found");
        }

        await createQuote({
          companyId,
          ownerId: user.id,
          accountId: payload.accountId,
          contactId: payload.contactId,
          opportunityId: payload.opportunityId,
          issueDate: payload.issueDate,
          validUntil: payload.validUntil,
          currencyCode: "NGN",
          notes: payload.notes,
          terms: payload.terms,
          items: payload.items,
        });

        await load();
      } catch (error) {
        setMessage(error.message || "Failed to create quote");
      } finally {
        setSaving(false);
      }
    },
    [companyId, load]
  );

  const filteredQuotes = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    return quotes.filter((quote) => {
      const matchesSearch =
        !searchValue ||
        String(quote.quote_number || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(quote.crm_accounts?.name || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(quote.crm_opportunities?.name || "")
          .toLowerCase()
          .includes(searchValue);

      const matchesStatus =
        statusFilter === "all" || String(quote.status || "") === statusFilter;

      const matchesAccount =
        accountFilter === "all" ||
        String(quote.account_id || "") === accountFilter;

      return matchesSearch && matchesStatus && matchesAccount;
    });
  }, [quotes, search, statusFilter, accountFilter]);

  return (
    <div>
      <h2 className="crm-page-title">Quotes</h2>

      {message ? <div className="crm-message">{message}</div> : null}

      <QuoteForm
        accounts={accounts}
        contacts={contacts}
        opportunities={opportunities}
        onSubmit={handleCreateQuote}
        saving={saving}
        prefillOpportunityId={prefillOpportunityId}
        defaultTerms={crmSettings?.default_quote_terms || ""}
        defaultNotes={crmSettings?.default_quote_notes || ""}
      />

      <div
        className="crm-table-card"
        style={{ marginTop: "24px", marginBottom: "20px" }}
      >
        <div className="crm-filter-row">
          <div className="crm-form-group">
            <label>Search</label>
            <input
              className="crm-input"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search quotes"
            />
          </div>

          <div className="crm-form-group">
            <label>Status</label>
            <select
              className="crm-select"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="converted">Converted</option>
            </select>
          </div>

          <div className="crm-form-group">
            <label>Account</label>
            <select
              className="crm-select"
              value={accountFilter}
              onChange={(event) => setAccountFilter(event.target.value)}
            >
              <option value="all">All</option>
              {accounts.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div>
        <h3 className="crm-page-title" style={{ fontSize: "20px" }}>
          Quote List
        </h3>

        {loading ? (
          <div className="crm-message">Loading quotes...</div>
        ) : filteredQuotes.length === 0 ? (
          <div className="crm-message">No quotes found.</div>
        ) : (
          filteredQuotes.map((quote) => (
            <div key={quote.id} className="crm-list-card">
              <h3 className="crm-list-title">
                {quote.quote_number || quote.id}
              </h3>
              <div className="crm-list-subtext">
                Account: {quote.crm_accounts?.name || "N/A"}
              </div>
              <div className="crm-list-subtext">
                Opportunity: {quote.crm_opportunities?.name || "N/A"}
              </div>
              <div className="crm-list-subtext">
                Status: {quote.status || "draft"}
              </div>
              <div className="crm-list-subtext">
                Total: ₦{Number(quote.total || 0).toLocaleString()}
              </div>

              <div className="crm-inline-actions">
                <Link className="crm-small-btn" to={`/crm/quotes/${quote.id}`}>
                  Open Quote
                </Link>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default QuotesPage;
